import React, { useState, useEffect, useRef, useCallback } from 'react'
import { useGoogleReCaptcha } from 'react-google-recaptcha-v3'
import SocialLogin from '../components/SocialLogin/SocialLogin'
import { API_ROOT, ERROR_MSGS, REGEX_EMAIL } from '../constants'
import { errors } from '../constants'
import { Button, Form, InputGroup, Spinner } from 'react-bootstrap'
import { useHistory } from 'react-router-dom'
import { validateDisallowedEmails } from 'utils/general'
import { isFreeEmailDomain } from 'utils/email'
import axios from 'axios'
import { getErrorMessage } from 'utils/general'
import celloService from '../services/cello'
import { withUtmParams } from '../utils/utm'

import {
  discoverSsoLogin,
  getSsoLoginStartUrl,
  loginWithEmail as loginWithEmailApi,
  loginWithPassword as loginWithPasswordApi,
  verifyOTPCode
} from '../services/auth'
import OTPInput from '../components/OTPInput'
import ConnectionDiagnosticsModal from '../components/ConnectionDiagnosticsModal/ConnectionDiagnosticsModal'
const LOGGED_IN = 'LOGGED_IN'
const REQUIRE_EMAIL = 'REQUIRE_EMAIL'
const REQUIRE_EMAIL_PASSWORD = 'REQUIRE_EMAIL_PASSWORD'
const REQUIRE_OTP = 'REQUIRE_OTP'
const REQUIRE_SSO = 'REQUIRE_SSO'
const LOGIN_SUCCESS = 'LOGIN_SUCCESS'
const LOGIN_ERROR = 'LOGIN_ERROR'
const LOGIN_ERROR_PASSWORD = 'LOGIN_ERROR_PASSWORD'
const OTP_ERROR = 'OTP_ERROR'
const LOADING = 'LOADING'
const REQUIRE_UPGRADE = 'REQUIRE_UPGRADE'

const OTP_LENGTH = 4

const DEFAULT_STATE = {
  stage: REQUIRE_EMAIL,
  error: undefined
}

const SignUpPage = () => {
  const [currentState, setCurrentState] = useState(DEFAULT_STATE)
  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [isValidEmail, setIsValidEmail] = useState(false)
  const [usePassword, setUsePassword] = useState(false)
  const [otpLoading, setOtpLoading] = useState(false)
  const [analysingConnection, setAnalysingConnection] = useState(false)
  const [diagnostics, setDiagnostics] = useState(null)
  const history = useHistory()
  const { executeRecaptcha } = useGoogleReCaptcha()
  const isLoginPage = window.location.pathname === '/login'

  useEffect(() => {
    if (localStorage.getItem('token')) {
      setCurrentState({ stage: LOGGED_IN })
    }

    // Pre-fill email from localStorage if available
    const savedEmail = localStorage.getItem('savedEmail')
    if (savedEmail) {
      setEmail(savedEmail)
      const isValidEmail =
        REGEX_EMAIL.test(savedEmail.toLowerCase()) && validateDisallowedEmails(savedEmail.toLowerCase())
      setIsValidEmail(isValidEmail)
    }

    // Check for error parameter in URL (from OAuth or other redirects)
    const urlParams = new URLSearchParams(window.location.search)
    const errorParam = urlParams.get('error')
    if (errorParam) {
      try {
        const decodedError = decodeURIComponent(errorParam)
        setCurrentState({ stage: LOGIN_ERROR, error: decodedError })
        // Clean up URL
        window.history.replaceState({}, '', '/login')
      } catch (e) {
        console.error('Failed to decode error parameter:', e)
      }
    }
  }, [])

  const onEmailChange = (e) => {
    const email = e.target.value
    const isValidEmail = REGEX_EMAIL.test(email.toLowerCase()) && validateDisallowedEmails(email.toLowerCase())
    setEmail(email)
    setIsValidEmail(isValidEmail)
  }

  const onPasswordChange = (e) => {
    const password = e.target.value
    setPassword(password)
  }

  const onUsePasswordChange = () => {
    setUsePassword(usePassword !== true)
    setCurrentState(usePassword === true ? { stage: REQUIRE_EMAIL } : { stage: REQUIRE_EMAIL_PASSWORD })
  }

  const handleLogin = () => {
    const { stage } = currentState
    if (stage === LOGGED_IN) {
      history.push('/manage')
    } else if (stage === REQUIRE_EMAIL) {
      continueWithEmail()
    } else if (stage === REQUIRE_SSO) {
      startSsoLogin()
    } else if (stage === REQUIRE_EMAIL_PASSWORD) {
      loginWithPassword()
    } else if (stage === LOGIN_SUCCESS || stage === LOGIN_ERROR || stage === LOGIN_ERROR_PASSWORD) {
      retryLogin()
    } else if (stage === REQUIRE_UPGRADE) {
      history.push('/pricing')
    }
  }

  const retryLogin = () => {
    setCurrentState(DEFAULT_STATE)
    setUsePassword(false)
  }

  const startSsoLogin = () => {
    window.location.assign(getSsoLoginStartUrl(email))
  }

  const continueWithEmail = async () => {
    if (!isLoginPage || isFreeEmailDomain(email)) {
      await loginWithEmail()
      return
    }

    setCurrentState({ stage: LOADING })

    try {
      const res = await discoverSsoLogin(email)
      if (res?.data?.sso === true) {
        setCurrentState({ stage: REQUIRE_SSO })
        return
      }
    } catch (e) {
      // Discover outage should not block magic-link login
    }

    await loginWithEmail()
  }

  const loginWithEmail = async () => {
    setCurrentState({ stage: LOADING })

    const data = withUtmParams({ email })

    const promoCode = localStorage.getItem('promoCode')
    if (promoCode) {
      data.promoCode = promoCode
    }

    const ucc = celloService.getCurrentUCC()
    if (ucc) {
      data.ucc = ucc
    }

    let recaptchaValue
    try {
      recaptchaValue = await executeRecaptcha('login')
    } catch (e) {
      setCurrentState({ stage: LOGIN_ERROR, error: errors.NO_CAPTCHA })
      return
    }
    const headers = { 'x-recaptcha': recaptchaValue }

    loginWithEmailApi(data, headers)
      .then(() => {
        setCurrentState({ stage: REQUIRE_OTP })
      })
      .catch((err) => {
        if (err?.response?.data?.code === errors.USER_TRIAL_EXPIRED) {
          setCurrentState({ stage: REQUIRE_UPGRADE })
        } else if (err?.response?.data?.code === errors.INVALID_EMAIL) {
          setCurrentState({ stage: LOGIN_ERROR, error: errors.INVALID_EMAIL })
        } else if (err?.response?.data?.code === errors.BANNED_SITE) {
          setCurrentState({ stage: LOGIN_ERROR, error: errors.BANNED_SITE })
        } else {
          setCurrentState({ stage: LOGIN_ERROR, error: err?.response?.data?.code })
        }
      })
  }

  const verifyOTP = useCallback(
    (code) => {
      setOtpLoading(true)
      setCurrentState({ stage: REQUIRE_OTP })

      verifyOTPCode(email, code)
        .then((res) => {
          if (res.data?.newAccount) {
            window.location = `/free-welcome?id=${res.data.setupToken}&email=${encodeURIComponent(email.toLowerCase().trim())}&trial=true`
          } else {
            localStorage.setItem('token', res.data.token)
            sessionStorage.removeItem('hasSkippedFreeTrialB2C')
            const redirectUrl = localStorage.getItem('redirect-url')
            if (redirectUrl) {
              localStorage.removeItem('redirect-url')
              window.location = redirectUrl
            } else {
              window.location = '/manage'
            }
          }
        })
        .catch(() => {
          setOtpLoading(false)
          setCurrentState({ stage: OTP_ERROR })
        })
    },
    [email]
  )

  const resendOTP = useCallback(async () => {
    const data = { email }
    const ucc = celloService.getCurrentUCC()
    if (ucc) {
      data.ucc = ucc
    }

    let recaptchaValue
    try {
      recaptchaValue = await executeRecaptcha('login')
    } catch (e) {
      setCurrentState({ stage: LOGIN_ERROR, error: errors.NO_CAPTCHA })
      return
    }
    const headers = { 'x-recaptcha': recaptchaValue }

    loginWithEmailApi(data, headers)
      .then(() => {
        setCurrentState({ stage: REQUIRE_OTP })
      })
      .catch(() => {
        setCurrentState({ stage: LOGIN_ERROR })
      })
  }, [email, executeRecaptcha])

  const loginWithPassword = async () => {
    try {
      const data = { email, password }

      const ucc = celloService.getCurrentUCC()
      if (ucc) {
        data.ucc = ucc
      }

      const res = await loginWithPasswordApi(data)

      history.push(res.data.authLink)
      setCurrentState({ stage: LOGIN_SUCCESS })
    } catch (error) {
      if (error?.response?.data?.code === errors.BANNED_SITE) {
        setCurrentState({ stage: LOGIN_ERROR, error: errors.BANNED_SITE })
      } else {
        setCurrentState({ stage: LOGIN_ERROR_PASSWORD, error: error?.response?.data?.code })
      }
    }
  }

  const analyseConnection = async () => {
    setAnalysingConnection(true)

    let reachable = false
    try {
      await axios.get(`${API_ROOT}/healthcheck`, { timeout: 10000 })
      reachable = true
    } catch (e) {
      reachable = false
    }

    setAnalysingConnection(false)
    setDiagnostics({ reachable })
  }

  const handleKeyDown = (e) => e.key === 'Enter' && isValidEmail && handleLogin()

  const onSignOut = () => {
    localStorage.removeItem('token')
    history.go(0)
  }

  const { stage, error } = currentState

  return (
    <div className="container">
      <h5 className="text-start">
        <b>{(isLoginPage ? 'Login or ' : '') + 'Sign up'}</b>
      </h5>
      <div className="text-start mt-3">
        {isLoginPage
          ? 'Manage your uploads from your dashboard.'
          : 'Sign up for an account to upload & manage your work.'}
      </div>
      <div className="box-100 p-3 login-box mt-4 border-light-grey">
        {stage === LOGGED_IN && <div className="login-message">You're logged in</div>}

        {(stage === REQUIRE_EMAIL || stage === REQUIRE_EMAIL_PASSWORD || stage === REQUIRE_SSO) && (
          <InputGroup className="input-group">
            <div className="p-1 mt-1 w-100">
              <Form.Control
                onChange={onEmailChange}
                onKeyPress={handleKeyDown}
                placeholder="Email"
                aria-label="email"
                autoFocus={true}
                type="email"
                value={email}
                disabled={stage === REQUIRE_SSO}
              />
            </div>

            {stage === REQUIRE_EMAIL_PASSWORD && (
              <div className="p-1 mt-1 w-100">
                <Form.Control
                  id="password-field"
                  onChange={onPasswordChange}
                  onKeyPress={handleKeyDown}
                  placeholder="Password"
                  aria-label="password"
                  type="password"
                  value={password}
                />
              </div>
            )}
          </InputGroup>
        )}

        {stage === REQUIRE_SSO && <div className="login-message mt-2">Your company uses SSO for this email.</div>}

        {(stage === REQUIRE_OTP || stage === OTP_ERROR) && (
          <OTPInput
            onComplete={verifyOTP}
            onResend={resendOTP}
            email={email}
            error={stage === OTP_ERROR}
            loading={otpLoading}
          />
        )}

        {stage === LOGIN_SUCCESS && (
          <div className="login-message">
            We've sent a login link to <b>{email}</b>
          </div>
        )}
        {stage === LOGIN_ERROR && (
          <div className="login-message">
            {getErrorMessage(error) || 'Sorry, unable to send you an email.'}
            <p className="link" onClick={() => window.$crisp.push(['do', 'chat:open'])}>
              <u>Contact support</u>
            </p>
          </div>
        )}

        {stage === LOGIN_ERROR_PASSWORD && (
          <div className="login-message">
            {getErrorMessage(error) || 'Wrong email or password'}
            <p className="link" onClick={() => window.$crisp.push(['do', 'chat:open'])}>
              <u>Contact support</u>
            </p>
          </div>
        )}
        {stage === REQUIRE_UPGRADE && (
          <div className="login-message ps-3 pe-3">{ERROR_MSGS[errors.USER_TRIAL_EXPIRED]}</div>
        )}
        {stage === LOGIN_ERROR && !getErrorMessage(error) && (
          <Button
            variant="outline-primary"
            className="w-100 mt-2"
            disabled={analysingConnection}
            onClick={analyseConnection}
          >
            {analysingConnection ? 'Analysing issue…' : 'Analyse issues'}
          </Button>
        )}

        {stage !== LOADING && stage !== REQUIRE_OTP && stage !== OTP_ERROR && (
          <Button
            className="action-btn w-100 mt-2"
            disabled={
              (stage !== LOGGED_IN && !isValidEmail) || (stage === REQUIRE_EMAIL_PASSWORD && isValidEmail && !password)
            }
            onClick={handleLogin}
          >
            {stage === LOGGED_IN && 'Continue'}
            {stage === REQUIRE_EMAIL && 'Email Me Login Link'}
            {stage === REQUIRE_SSO && 'Sign in with company SSO'}
            {stage === REQUIRE_EMAIL_PASSWORD && 'Login'}
            {stage === LOGIN_SUCCESS && 'Try again'}
            {stage === LOGIN_ERROR && 'Try again'}
            {stage === LOGIN_ERROR_PASSWORD && 'Try again'}
            {stage === REQUIRE_UPGRADE && 'Upgrade to Pro'}
          </Button>
        )}

        {stage === REQUIRE_SSO && (
          <div className="mt-1 text-center">
            <a className="font-small link" onClick={() => loginWithEmail()}>
              Use email login instead
            </a>
          </div>
        )}

        {(stage === REQUIRE_EMAIL || stage === REQUIRE_EMAIL_PASSWORD) && (
          <div className="mt-1 text-center">
            <a className="font-small link" onClick={onUsePasswordChange}>
              {usePassword ? 'Login with email link' : 'Login with password'}
            </a>
          </div>
        )}

        {stage === LOGGED_IN && (
          <div className="mt-2 font-small">
            or{' '}
            <span className="link" onClick={onSignOut}>
              Sign out
            </span>
          </div>
        )}
        {stage === LOADING && (
          <div className="w-100 text-center">
            <Spinner className="mt-3 ms-auto mr-auto" animation="border" />
          </div>
        )}
        {(stage === REQUIRE_EMAIL || stage === REQUIRE_EMAIL_PASSWORD) && (
          <>
            <div className="separator mt-3 w-75 ms-auto me-auto">Or Login with</div>
            <SocialLogin className="mt-3" />
          </>
        )}
      </div>
      <div className="toc">
        By using our service you accept our{' '}
        <a href="https://tiiny.host/termsofservice.html" target="_blank" rel="noreferrer">
          <u>terms & conditions</u>
        </a>{' '}
        &{' '}
        <a href="https://tiiny.host/privacypolicy.html" target="_blank" rel="noreferrer">
          <u>privacy policy</u>
        </a>
      </div>
      <ConnectionDiagnosticsModal
        show={!!diagnostics}
        reachable={diagnostics?.reachable}
        onHide={() => setDiagnostics(null)}
      />
    </div>
  )
}

export default SignUpPage
